"use client";
import { useState, useEffect } from "react";
import { ModalCustom } from "../../../components/custom/modal-custom";
import {
  EQUIPMENT_TYPE,
  EQUIPMENT_TYPE_NAME,
  EQUIPMENT_STATUS,
  EQUIPMENT_STATUS_NAME,
} from "../services/equipment-service";
import { EquipmentRow } from "./equipment-table";

export interface EquipmentFormValues {
  code: string;
  name?: string;
  type: EQUIPMENT_TYPE;
  status?: EQUIPMENT_STATUS;
  capacity?: number;
  note?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: EquipmentFormValues) => Promise<boolean>;
  loading?: boolean;
  editData?: EquipmentRow | null;
}

const EquipmentCreateModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  editData,
}) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState<EQUIPMENT_TYPE>(EQUIPMENT_TYPE.BAG);
  const [status, setStatus] = useState<EQUIPMENT_STATUS>(EQUIPMENT_STATUS.AVAILABLE);
  const [capacity, setCapacity] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (editData) {
      setCode(editData.code);
      setName(editData.name || "");
      setType(editData.type as EQUIPMENT_TYPE);
      setStatus(editData.status as EQUIPMENT_STATUS);
      setCapacity(editData.capacity ? String(editData.capacity) : "");
      setNote(editData.note || "");
    } else {
      setCode("");
      setName("");
      setType(EQUIPMENT_TYPE.BAG);
      setStatus(EQUIPMENT_STATUS.AVAILABLE);
      setCapacity("");
      setNote("");
    }
  }, [editData, isOpen]);

  const handleSubmit = async () => {
    if (!code.trim()) return;
    const values: EquipmentFormValues = {
      code: code.trim(),
      name: name.trim() || undefined,
      type,
      capacity: capacity ? Number(capacity) : undefined,
      note: note.trim() || undefined,
    };
    if (editData) {
      values.status = status;
    }
    const ok = await onSubmit(values);
    if (ok) onClose();
  };

  const bodyModal = (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">
          Mã thiết bị <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={!!editData}
          placeholder="Nhập mã thiết bị (quét QR hoặc barcode đều dùng mã này)"
          className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
        />
        <p className="text-xs text-gray-500 mt-1">
          Mã này dùng để quét trên app (QR hoặc barcode đều cho ra mã này)
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">
          Tên thiết bị
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="VD: Túi giặt 01, Máy giặt Samsung (tùy chọn)"
          className="w-full border rounded px-3 py-2 text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Loại thiết bị</label>
          <select
            value={type}
            onChange={(e) => setType(Number(e.target.value) as EQUIPMENT_TYPE)}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            {Object.entries(EQUIPMENT_TYPE_NAME).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        {editData && (
          <div>
            <label className="block text-sm font-medium mb-1">Trạng thái</label>
            <select
              value={status}
              onChange={(e) => setStatus(Number(e.target.value) as EQUIPMENT_STATUS)}
              className="w-full border rounded px-3 py-2 text-sm"
            >
              {Object.entries(EQUIPMENT_STATUS_NAME).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Sức chứa (kg)</label>
        <input
          type="number"
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          placeholder="VD: 10"
          className="w-full border rounded px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Ghi chú</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ghi chú thêm..."
          rows={2}
          className="w-full border rounded px-3 py-2 text-sm"
        />
      </div>
    </div>
  );

  const footerModal = (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
      >
        Hủy
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !code.trim()}
        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? "Đang xử lý..." : editData ? "Cập nhật" : "Tạo mới"}
      </button>
    </div>
  );

  return (
    <ModalCustom
      titleModal={editData ? "Cập nhật thiết bị" : "Thêm thiết bị mới"}
      isOpen={isOpen}
      onClose={() => onClose()}
      bodyModal={bodyModal}
      footerModal={footerModal}
      modalSize="lg"
    />
  );
};

export default EquipmentCreateModal;
